import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ContentCard } from "@/components/ui/ContentCard";
import { cn } from "@/lib/utils";
import { SCENARIO_PAGE_SHELL } from "./scenarioPageLayout";
import {
  TEACHER_SCENARIO_CATEGORIES,
  TEACHER_SCENARIO_STAGES,
  type TeacherScenario,
  type TeacherScenarioMode,
} from "@/config/teacherScenarios";
import { getVisibleStages } from "./ScenarioStageTracker";
import { getKnowledgeProfileForScenario } from "@/config/teacherScenarioKnowledge";

const MODE_COPY: Record<TeacherScenarioMode, { label: string; description: string }> = {
  prep: {
    label: "Prep mode",
    description:
      "Anticipate the hard moment, sharpen your language, and build regulation strategies — no full role-play unless you ask for it.",
  },
  practice: {
    label: "Practice mode",
    description:
      "Describe your plan first. After coaching, you'll role-play the scenario with mid-conversation twists.",
  },
};

interface ScenarioBriefCardProps {
  scenario: TeacherScenario;
  mode?: TeacherScenarioMode | null;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** When false, always show full brief (used on the selection screen). */
  collapsible?: boolean;
}

function BriefSections({
  scenario,
  mode,
}: {
  scenario: TeacherScenario;
  mode: TeacherScenarioMode | null;
}) {
  const stages = mode ? getVisibleStages(mode) : TEACHER_SCENARIO_STAGES.filter((s) => s.id !== "setup");
  const knowledge = getKnowledgeProfileForScenario(scenario);
  const modeCopy = mode ? MODE_COPY[mode] : null;

  return (
    <div className="space-y-4">
      <section>
        <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">The situation</p>
        <p className="mt-1 font-inter text-sm leading-relaxed text-gray-800">{scenario.openingPrompt}</p>
      </section>

      <section>
        <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">Why this matters</p>
        <p className="mt-1 font-inter text-sm text-gray-700">{scenario.summary}</p>
      </section>

      {modeCopy ? (
        <section>
          <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">{modeCopy.label}</p>
          <p className="mt-1 font-inter text-sm text-gray-700">{modeCopy.description}</p>
        </section>
      ) : (
        <section>
          <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">Mode</p>
          <p className="mt-1 font-inter text-sm text-gray-600">
            Choose Prep or Practice below to see how this session will run.
          </p>
        </section>
      )}

      <section>
        <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">What happens next</p>
        <ol className="mt-2 space-y-1">
          {stages.map((stage, idx) => (
            <li key={stage.id} className="flex gap-2 font-inter text-sm text-gray-700">
              <span className="font-semibold text-bodhi-teal">{idx + 1}.</span>
              <span>
                <span className="font-medium">{stage.label}</span>
                <span className="text-gray-500"> — {stage.micro}</span>
              </span>
            </li>
          ))}
        </ol>
      </section>

      <section>
        <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">Coaching focus</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 font-inter text-sm text-gray-700">
          {scenario.coachingGates.map((gate) => (
            <li key={gate}>{gate}</li>
          ))}
        </ul>
      </section>

      <section>
        <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">HeyBodhi frameworks in play</p>
        <p className="mt-1 font-inter text-xs text-gray-600">{knowledge.frameworkIds.join(" · ")}</p>
      </section>
    </div>
  );
}

export const ScenarioBriefCard = ({
  scenario,
  mode = null,
  collapsed = false,
  onCollapsedChange,
  collapsible = true,
}: ScenarioBriefCardProps) => {
  const category = TEACHER_SCENARIO_CATEGORIES.find((c) => c.id === scenario.category);

  const header = (
    <div className="min-w-0">
      <p className="font-inter text-[10px] font-semibold uppercase tracking-widest text-bodhi-teal/80">
        {category?.label ?? scenario.category}
      </p>
      <h3 className="mt-1 font-literata text-lg font-semibold text-bodhi-teal">{scenario.title}</h3>
    </div>
  );

  if (!collapsible) {
    return (
      <ContentCard className={SCENARIO_PAGE_SHELL.card}>
        {header}
        <div className="mt-4">
          <BriefSections scenario={scenario} mode={mode} />
        </div>
      </ContentCard>
    );
  }

  return (
    <Collapsible open={!collapsed} onOpenChange={(open) => onCollapsedChange?.(!open)}>
      <ContentCard className={cn(SCENARIO_PAGE_SHELL.card, collapsible && "p-4")}>
        <CollapsibleTrigger className="flex w-full items-start justify-between gap-3 text-left">
          {header}
          <ChevronDown
            className={cn("mt-1 h-4 w-4 shrink-0 text-bodhi-teal/70 transition-transform", !collapsed && "rotate-180")}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-4">
            <BriefSections scenario={scenario} mode={mode} />
          </div>
        </CollapsibleContent>
      </ContentCard>
    </Collapsible>
  );
};

export function buildModeAwareWelcome(scenario: TeacherScenario, mode: TeacherScenarioMode): string {
  if (mode === "prep") {
    return `You're preparing for **${scenario.title}**. Read the brief above, then tell me how you'd anticipate this situation — what's your opening move and what do you want to stay regulated?`;
  }
  return `You're practicing **${scenario.title}**. Read the brief above, then describe how you'd handle this — what's your opening move? After we coach your plan, we'll role-play with possible twists.`;
}
